const { getElement, getElements } = window.BAO.Utils

export class SiteDrawers extends window.BAO.CustomElement() {
  constructor () {
    super()

    if (this.hasAlreadyBeenUsed) {
      throw new Error(
        `You can only use 1 instance of ${this.nodeName.toLowerCase()}`,
      )
    }

    this._activeDrawerKey = ''
    this._transitionAbort = null
    this._closingDrawer = null
    this.drawers = []

    window.drawers = this

    this.drawerKeys
      .filter((key) => !this.triggerKeys.includes(key))
      .forEach((key) =>
        console.info(`Drawer exists for ${key} but there is no trigger`),
      )
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      document.documentElement,
      'click',
      this.handleClick.bind(this),
    )

    this.listeners.add(
      document.documentElement,
      'keydown',
      this.handleKeydown.bind(this),
    )
  }

  /**
   * Closes the filters drawer when Escape is pressed.
   * Scoped to `.drw-Drawer-filters` only, matching handleFocusManagement.
   *
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  handleKeydown (e) {
    if (e.key !== 'Escape') return

    const openDrawer = this.drawers.find((drawer) => drawer.isOpen)
    if (!openDrawer?.classList.contains('drw-Drawer-filters')) return

    const key = this.activeDrawerKey

    window.BAO.dispatchEvent(window.BAO.EVENTS.DRAWER.CLOSED, { key })

    this.activeDrawerKey = ''
  }

  handleClick (e) {
    const EVENTS = {
      TRIGGER: 'TRIGGER',
      CLOSE: 'CLOSE',
      BACKDROP: 'BACKDROP',
      UNKNOWN: 'UNKNOWN',
    }
    const eventType = (() => {
      if (e.target.closest(this.config.matches.trigger) !== null) {
        return EVENTS.TRIGGER
      } else if (e.target.closest(this.config.matches.close) !== null) {
        return EVENTS.CLOSE
      } else if (e.target.closest(this.config.matches.backdrop) !== null) {
        return EVENTS.BACKDROP
      }

      return EVENTS.UNKNOWN
    })()

    if (eventType === EVENTS.UNKNOWN) return
    e.preventDefault()

    if (eventType === EVENTS.TRIGGER) {
      const trigger = e.target.closest(this.config.matches.trigger)
      const key = trigger.getAttribute('data-drawers-trigger')

      this.activeDrawerKey = key === this.activeDrawerKey ? '' : key

      window.BAO.dispatchEvent(window.BAO.EVENTS.DRAWER.OPENED, {
        key,
        trigger,
      })
    }

    if (eventType === EVENTS.CLOSE || eventType === EVENTS.BACKDROP) {
      const key = this.activeDrawerKey

      window.BAO.dispatchEvent(window.BAO.EVENTS.DRAWER.CLOSED, {
        key,
      })

      this.activeDrawerKey = ''
    }
  }

  /**
   * @param {string} key
   */
  open (key) {
    this.activeDrawerKey = key
  }

  close () {
    this.activeDrawerKey = ''
  }

  get triggerKeys () {
    return getElements('[data-drawers-trigger]').map((el) =>
      el.getAttribute('data-drawers-trigger'),
    )
  }

  get drawerKeys () {
    return getElements('[data-module-drawers-drawer]').map((el) =>
      el.getAttribute('data-module-drawers-drawer'),
    )
  }

  get hasAlreadyBeenUsed () {
    const el = getElement(this.nodeName.toLowerCase())

    return el !== this
  }

  get aDrawerIsOpen () {
    return this.drawers.some((drawer) => drawer.isOpen)
  }

  get activeDrawerKey () {
    return this._activeDrawerKey
  }

  set activeDrawerKey (key) {
    if (this._transitionAbort) {
      this._transitionAbort.abort()
      this._transitionAbort = null
    }

    const openDrawer = this.drawers.find((d) => d.isOpen)
    const drawerToWaitFor = openDrawer ?? this._closingDrawer

    if (key !== '' && drawerToWaitFor) {
      if (openDrawer) {
        this.activeDrawerKey = ''
      }

      const controller = new AbortController()
      this._transitionAbort = controller

      const onTransitionEnd = (e) => {
        if (e.propertyName !== 'clip-path') return
        drawerToWaitFor.removeEventListener('transitionend', onTransitionEnd)
        this._transitionAbort = null
        this.activeDrawerKey = key
      }

      drawerToWaitFor.addEventListener('transitionend', onTransitionEnd, {
        signal: controller.signal,
      })

      return
    }

    if (key === '') {
      const wasOpen = this.drawers.find((d) => d.isOpen)
      if (wasOpen) {
        this._closingDrawer = wasOpen
        const clearWhenDone = (e) => {
          if (e.propertyName !== 'clip-path') return
          wasOpen.removeEventListener('transitionend', clearWhenDone)
          if (this._closingDrawer === wasOpen) this._closingDrawer = null
        }
        wasOpen.addEventListener('transitionend', clearWhenDone)
      }
    } else {
      this._closingDrawer = null
    }

    this._activeDrawerKey = key

    this.drawers.forEach((drawer) => (drawer.isOpen = drawer.key === key))

    this.handleFocusManagement(key)

    this.config.elements.drawers.classList.toggle(
      this.config.classes.activeClass,
      this.aDrawerIsOpen,
    )
    document.body.classList.toggle(
      this.config.classes.siteOverflowed,
      this.aDrawerIsOpen,
    )
    document.body.classList.toggle(
      this.config.classes.drawersOpenUtility,
      this.aDrawerIsOpen,
    )

    if (key !== '') {
      this.els.drawer.element.classList.add('drw-Drawers_Drawer-active')
      document.body.classList.add(`drw-Drawers-${key}`)
    } else {
      this.els.drawer.element.classList.remove('drw-Drawers_Drawer-active')
    }

    setTimeout(() => {
      document.body.classList.toggle(
        this.config.classes.drawersOpenDelay,
        this.aDrawerIsOpen,
      )
    }, 4000)
  }

  /**
   * Moves focus into the drawer and traps it there while open, and returns
   * focus to whatever triggered the drawer once it closes. Scoped to
   * `.drw-Drawer-filters` only.
   *
   * @param {string} key
   *
   * @returns {void}
   */
  handleFocusManagement (key) {
    if (key !== '') {
      const openedDrawer = this.drawers.find((drawer) => drawer.key === key)
      if (!openedDrawer?.classList.contains('drw-Drawer-filters')) return

      this._filtersFocusReturnEl = document.activeElement

      window.BAO.Utils.trapFocus(openedDrawer)

      openedDrawer.querySelector(this.config.matches.focusable)?.focus()

      return
    }

    if (!this._filtersFocusReturnEl) return

    window.BAO.Utils.disableTrapFocus()
    this._filtersFocusReturnEl.focus()
    this._filtersFocusReturnEl = null
  }

  get config () {
    return {
      matches: {
        trigger: '[data-drawers-trigger]',
        subNav: '[data-drawers-sub-nav]',
        back: '[data-drawers-back]',
        close: '[data-drawers-close]',
        backdrop: '[data-site-drawers-el="backdrop"]',
        focusable:
          'button:not([disabled]), a[href]:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])',
      },
      classes: {
        activeClass: 'drw-Drawers-active',
        activeDrawerClass: 'drw-Drawer-active',
        siteOverflowed: 'util-SiteOverflowed',
        drawersOpenUtility: 'util-DrawersOpen',
        drawersOpenDelay: 'util-DrawersOpenDelay',
      },
      elements: {
        body: document.body || document.documentElement,
        backdrop: '[data-site-drawers-el="backdrop"]',
        drawers: document.querySelector('site-drawers'),
      },
    }
  }
}

customElements.define('site-drawers', SiteDrawers)
